import { HttpResponse } from '@angular/common/http';
import {
    AddressDTO,
    AddressType,
    AttributesValueType,
    CaseStatusType,
    CompanyAttributeDTO,
    CompanyDTO,
    CompanyRegistrationPeriodDTO,
    CompanyRole,
    ContactInformationType,
    DocumentDescriptionDTO,
    EmailDTO,
    InvoiceStatus,
    OfficeDTO,
    PersonDTO,
    PhoneNumberDTO,
    ProductCategoryType,
    RestrictionDTO,
    RestrictionOperator
} from '@iota/iota-management-portal-v4-client';
import {
    AddressDTO as InhCoreAddressDTO,
    BasicEmployeeView,
    ManagementSubscriptionDTO,
    PaymentDTO,
    PayrollReportSummaryStatus,
    RequestCategoryLevel1
} from '@iota/inh-core-v1-management-client';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { catchError, filter, take, throwError } from 'rxjs';
import * as XLSX from 'xlsx';
import { AuditInvoiceSummaryEntity, CompanyEntity, EmployeeRequestEntity, PayrollReportSummaryEntity } from '../store/entity-models';
import { SmartTableHeader, SmartTableRow } from './components/smart-table/smart-table.component';
import { ProductAttribute, RegistrationPeriodAttribute } from './constants-and-enums/attribute-types';
import { ChartPeriodName, CustomChartData } from './constants-and-enums/chart-type';
import { IotaConfigAttribute } from './constants-and-enums/iota-config';
import { AvailableLanguages, DefaultLanguage, InhLanguage, InhLanguageCountryCode, InhLanguages } from './constants-and-enums/language';
import { NaceCodesRev2Descriptions } from './constants-and-enums/nace-code';
import { RequestDocumentStatusLabel } from './constants-and-enums/request-document-labels';
import { companies, companiesLoading } from '../store/reducer/company.reducer';
import { CountryCode } from './constants-and-enums/country-code';
import { AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER } from './constants-and-enums/audit-invoice-types';
import { PAYROLL_REPORT_SUMMARY_OVERVIEW_FILTER } from './constants-and-enums/payroll-report-summary-types';
import { Actions, ofType } from '@ngrx/effects';
import { Action, ActionCreator } from '@ngrx/store';
import { FeedbackBannerVariant } from './components/feedback-banner/feedback-banner.component';

export const ATTRIBUTE_EXPORT_SEPARATOR = ';';

const SDK_GESUNDWERKER_PRODUCT_CATEGORIES: string[] = [
    ProductCategoryType.SDK_GESUNDWERKER_PREVENTIVE_CARE,
    ProductCategoryType.SDK_GESUNDWERKER_AMBULATORY_BUDGET,
    ProductCategoryType.SDK_GESUNDWERKER_DENTAL_BUDGET
];

// TODO: remove the SDK_GESUNDWERKER_* spread once dedicated SDK submission categories exist and these no longer submit under health-budget.
const HEALTH_BUDGET_PRODUCT_CATEGORIES: string[] = [
    ProductCategoryType.HEALTH_CARE,
    ProductCategoryType.HEALTH_BENEFIT,
    ProductCategoryType.HEALTH_BUNDLE,
    ProductCategoryType.HEALTH_COMBI,
    ProductCategoryType.XTEND,
    ...SDK_GESUNDWERKER_PRODUCT_CATEGORIES
];

// SW - refactored functions to be out of class: https://medium.com/@OlegVaraksin/what-is-the-best-way-to-write-utilities-in-typescript-e3cae916fe30

export function isEmptyOrNull(object: any): boolean {
    return object === null || object === undefined || JSON.stringify(object) === '{}';
}

export function isNotNullOrUndefined<T>(value: T | null | undefined): value is T {
    return value !== null && value !== undefined;
}

export function isValidNumber(value: any): value is number {
    return value !== null && value !== undefined && typeof value === 'number' && !isNaN(value);
}

export function isValidString(value: any): value is string {
    return value !== null && value !== undefined && typeof value === 'string' && value.trim() !== '';
}

export function getPersonEmail(person: PersonDTO): EmailDTO {
    if (person && person.emails && person.emails.length > 0) {
        if (person.emails.some((e) => e.type === ContactInformationType.WORKPLACE)) {
            return person.emails.find((e) => e.type === ContactInformationType.WORKPLACE);
        } else {
            return person.emails[0];
        }
    }
    return {} as EmailDTO;
}

export function getPersonPhoneNumber(person: PersonDTO): PhoneNumberDTO {
    if (person && person.phoneNumbers && person.phoneNumbers.length > 0) {
        if (person.phoneNumbers.some((e) => e.type === ContactInformationType.WORKPLACE)) {
            return person.phoneNumbers.find((e) => e.type === ContactInformationType.WORKPLACE);
        } else {
            return person.phoneNumbers[0];
        }
    }
    return {} as PhoneNumberDTO;
}

export function parseMainAddress(addresses: (AddressDTO | InhCoreAddressDTO)[]): string {
    if (!addresses || addresses.length === 0) {
        return '';
    }
    if (addresses.length === 1) {
        return parseAddressText(addresses[0]);
    }
    const mainAddress = addresses.find((a) => a.addressType === AddressType.MAIN_ADDRESS);
    if (mainAddress) {
        return parseAddressText(mainAddress);
    }
    return parseAddressText(addresses[0]);
}

export function parseAddressText(address: AddressDTO | InhCoreAddressDTO): string {
    if (!address) {
        return '';
    }

    const streetLine = [address.street, address.houseNumber].filter(Boolean).join(' ');
    const countryPrefix = address.countryCode && address.countryCode !== 'DE' ? address.countryCode + '-' : '';
    const cityLine = [countryPrefix + (address.zipCode ?? ''), address.city].filter(Boolean).join(' ');

    return [streetLine, cityLine].filter(Boolean).join(', ');
}

export function caseStatusToBadgeClass(status: CaseStatusType | string, isPreventionCourseCase = false) {
    if (status === CaseStatusType.CHECKING) {
        return 'bg-iota-draft text-gray-500';
    } else if (status === CaseStatusType.READY_TO_TRANSFER) {
        return 'bg-iota-requested text-yellow-700';
    } else if (status === CaseStatusType.TRANSFERED) {
        return isPreventionCourseCase ? 'bg-iota-inh-green' : 'bg-iota-warning';
    } else if (status === CaseStatusType.CLAIM_APPROVED) {
        return 'bg-iota-inh-green';
    } else if (status === CaseStatusType.PARTLY_REFUNDED) {
        return 'bg-iota-inh-green';
    } else if (status === CaseStatusType.COMPLETELY_REFUNDED) {
        return 'bg-iota-inh-green';
    } else if (status === CaseStatusType.DISCARDED) {
        return 'bg-iota-inh-green';
    } else if (status === CaseStatusType.DENIED) {
        return 'bg-iota-rejected text-red-700';
    } else if (status === CaseStatusType.CLAIM_DENIED) {
        return 'bg-iota-rejected text-red-700';
    } else if (status === CaseStatusType.IN_CLAIM) {
        return 'bg-iota-warning';
    } else {
        return 'bg-iota-warning';
    }
}

export function caseStatusToName(status: CaseStatusType | string) {
    if (status === CaseStatusType.CHECKING) {
        // Explicit id: the plain source "In progress" resolves to a differently translated unit.
        return $localize`:@@caseStatusInProgress:In progress`;
    } else if (status === CaseStatusType.COMPLETELY_REFUNDED) {
        return $localize`Reimbursed`;
    } else if (status === CaseStatusType.PARTLY_REFUNDED) {
        return $localize`Partly reimbursed`;
    } else if (status === CaseStatusType.DENIED) {
        return $localize`Rejected`;
    } else if (status === CaseStatusType.DISCARDED) {
        return $localize`Discarded`;
    } else if (status === CaseStatusType.READY_TO_TRANSFER) {
        return $localize`Released for export`;
    } else if (status === CaseStatusType.TRANSFERED) {
        return $localize`Sent`;
    } else if (status === CaseStatusType.IN_CLAIM) {
        return $localize`Complained`;
    } else if (status === CaseStatusType.CLAIM_APPROVED) {
        return $localize`Complaint approved`;
    } else if (status === CaseStatusType.CLAIM_DENIED) {
        return $localize`Complaint rejected`;
    } else {
        // dürfte es nicht geben -> Aufmerksam machen
        return $localize`ERROR - please contact!`;
    }
}

export type StatusEnum = 'PROCESSING' | 'COMPLETED' | 'DENIED';
export const StatusEnum = {
    PROCESSING: 'PROCESSING' as StatusEnum,
    COMPLETED: 'COMPLETED' as StatusEnum,
    DENIED: 'DENIED' as StatusEnum
};

export function formatB2CPaymentStatus(status: PaymentDTO.StatusEnum): string {
    switch (status) {
        case 'SUCCEEDED':
            return $localize`Succeeded`;
        case 'PENDING':
            return $localize`Pending`;
        case 'FAILED':
            return $localize`Failed`;
        default:
            return status;
    }
}

export function formatB2CPaymentMethod(method: PaymentDTO.MethodEnum): string {
    switch (method) {
        case 'CREDIT_CARD':
            return $localize`Credit card`;
        case 'PAYPAL':
            return $localize`PayPal`;
        case 'OTHER':
            return $localize`Other`;
        default:
            return method;
    }
}

export const B2C_PAYMENT_STATUS_TO_BADGE_CLASS: { [key in PaymentDTO.StatusEnum]: string } = {
    SUCCEEDED: 'bg-iota-inh-green text-white',
    PENDING: 'bg-iota-warning text-white',
    FAILED: 'bg-iota-rejected text-red-700'
};

export function transactionStatusToBadgeClass(status: StatusEnum | string) {
    if (status === StatusEnum.PROCESSING) {
        return 'bg-iota-warning';
    } else if (status === StatusEnum.COMPLETED) {
        return 'bg-iota-inh-green';
    } else if (status === StatusEnum.DENIED) {
        return 'bg-iota-rejected text-red-700';
    } else {
        return 'bg-iota-warning';
    }
}

export function transactionStatusToName(status: StatusEnum | string) {
    if (status === StatusEnum.PROCESSING) {
        return $localize`To be transferred`;
    } else if (status === StatusEnum.COMPLETED) {
        return $localize`Transferred`;
    } else if (status === StatusEnum.DENIED) {
        return $localize`Rejected`;
    } else {
        // dürfte es nicht geben -> Aufmerksam machen
        return $localize`ERROR - please contact!`;
    }
}

export function getNaceCodeDescription(naceCode: string) {
    return NaceCodesRev2Descriptions.get(naceCode);
}

export function replaceRestrictionListSeparator(restriction: RestrictionDTO) {
    if (restriction.operator === RestrictionOperator.IN) {
        const pieces = restriction.value.split(',');
        restriction.value = pieces.join('|');
    }
}

export function exportArrayToExcel(arr: any[], name?: string): void {
    const { sheetName, fileName } = getFileName(name);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(arr);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${fileName}.xlsx`);
}

export function getFileName(name: string): any {
    const timeSpan = new Date().toISOString();
    const sheetName = name || 'ExportResult';
    const fileName = `${sheetName}-${timeSpan}`;
    return {
        sheetName,
        fileName
    };
}

interface AttributeDTOCommon {
    [key: string]: any;

    attributeName?: string;
    booleanValue?: Array<boolean>;
    dateTimeValue?: Array<Date>;
    dateValue?: Array<Date>;
    intValue?: Array<number>;
    numValue?: Array<number>;
    strValue?: Array<string>;
    uuidValue?: Array<string>;
    valueType?: AttributesValueType;
}

interface AttributeDetailsCommon {
    [key: string]: {
        [key: string]: any;
        valueType?: AttributesValueType;
        value?: any[];
    };
}

export function getAttributeFirstValue(
    entityWithAttributes: {
        [key: string]: any;
        attributes?: AttributeDetailsCommon | AttributeDTOCommon[];
    },
    attributeName: string
): any {
    if (!entityWithAttributes) {
        return undefined;
    }
    const values = getAttributeValues(entityWithAttributes, attributeName);
    if (values && values.length > 0) {
        const firstValue = values[0];
        return firstValue === null ? undefined : firstValue;
    }
    return undefined;
}

/** Accepts anything carrying merged product attributes, not only a product itself. */
export function isDiscontinued(entityWithAttributes: {
    [key: string]: any;
    attributes?: AttributeDetailsCommon | AttributeDTOCommon[];
}): boolean {
    const discontinuedAttribute = getAttributeFirstValue(entityWithAttributes, ProductAttribute.PRODUCT_DISCONTINUED);

    return !!discontinuedAttribute && moment.utc(discontinuedAttribute).isBefore(moment.utc(), 'day');
}

// in case a different Entity with Attributes is used, incoherent with CompanyAttributeDTO,
// might make sense to change CompanyAttributeDTO to a Common attribute class with extension of locale
export function getAttributeValueInDefaultLanguage(
    entityWithAttributes: {
        [key: string]: any;
        attributes?: CompanyAttributeDTO[];
    },
    attributeName: string,
    defaultLanguage = 'de'
): any {
    return (
        getLocalizedAttribute(entityWithAttributes, attributeName)[defaultLanguage] ??
        getAttributeFirstValue(entityWithAttributes, attributeName)
    );
}

export function getLocalizedAttribute(
    entityWithAttributes: {
        [key: string]: any;
        attributes?: CompanyAttributeDTO[];
    },
    attributeName: string
): { [locale: string]: string } {
    const valuesForLocales: { [locale: string]: string } = {};
    if (!entityWithAttributes.attributes) {
        return {};
    }
    const attributes = entityWithAttributes.attributes.filter((it) => it.attributeName === attributeName);
    attributes.forEach((it) => (valuesForLocales[it.locale] = it.strValue[0]));

    return valuesForLocales;
}

export function getAttributeValues(
    entityWithAttributes: {
        [key: string]: any;
        attributes?: AttributeDetailsCommon | AttributeDTOCommon[];
    },
    attributeName: string
): any[] {
    if (!entityWithAttributes.attributes) {
        return [];
    }
    const tryAttributeByKey = entityWithAttributes.attributes[attributeName];
    if (tryAttributeByKey) {
        // if we find an attribute by key assume the structure contains AttributeDetails
        const attributes = entityWithAttributes.attributes as AttributeDetailsCommon;
        if (attributes) {
            return attributes[attributeName].value;
        }
    } else {
        // else try to look if it's an AttributeDTO
        const attributes = entityWithAttributes.attributes as AttributeDTOCommon[];
        if (attributes && attributes.length > 0) {
            const attributeByName = attributes.filter((attribute) => attribute.attributeName === attributeName)[0];

            if (!attributeByName) {
                return undefined;
            }
            if (attributeByName.valueType === AttributesValueType.BOOL) {
                return attributeByName.booleanValue;
            }
            if (attributeByName.valueType === AttributesValueType.DATE) {
                return attributeByName.dateValue;
            }
            if (attributeByName.valueType === AttributesValueType.DATETIME) {
                return attributeByName.dateTimeValue;
            }
            if (attributeByName.valueType === AttributesValueType.INTEGER) {
                return attributeByName.intValue;
            }
            if (attributeByName.valueType === AttributesValueType.NUMBER) {
                return attributeByName.numValue;
            }
            if (attributeByName.valueType === AttributesValueType.STRING) {
                return attributeByName.strValue;
            }
            if (attributeByName.valueType === AttributesValueType.UUID) {
                return attributeByName.uuidValue;
            }
        }
    }
    return undefined;
}

export function formatBytes(bytes, decimals = 2): string {
    if (bytes === 0) {
        return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function subscriptionFitsToRequest(subscription: ManagementSubscriptionDTO, request: EmployeeRequestEntity): boolean {
    if (!request?.categoryLevel1) {
        return false;
    }

    return subscriptionFitsToRequestCategoryLevel1(subscription, request.categoryLevel1);
}

function subscriptionFitsToRequestCategoryLevel1(subscription: ManagementSubscriptionDTO, category: RequestCategoryLevel1): boolean {
    switch (category) {
        case 'HEALTHBUDGET':
            return HEALTH_BUDGET_PRODUCT_CATEGORIES.includes(subscription.productCategory);
        case 'PREVENTIONCOURSES':
            return false;
        case 'PREVENTIONCOURSES_PLUS':
            return subscription.productCategory === 'PREVENTION_COURSES';
        case 'XTEND':
            return HEALTH_BUDGET_PRODUCT_CATEGORIES.includes(subscription.productCategory);
        default:
            return false;
    }
}

/**
 * rounds the given number
 * @param num the number to round
 * @param decimalPlaces the decimal places after rounding, defaults to 2
 * @returns rounded number
 */
export function round(num: number, decimalPlaces = 2): number {
    const factor: number = Math.pow(10, decimalPlaces);

    return Math.round((num + Number.EPSILON) * factor) / factor;
}

export function requestDocumentStatusToBorderClass(status: RequestDocumentStatusLabel) {
    switch (status) {
        case RequestDocumentStatusLabel.APPROVED:
            return 'border-iota-inh-green';
        case RequestDocumentStatusLabel.DISCARDED:
            return 'border-iota-inh-green';
        case RequestDocumentStatusLabel.REQUEST_RESUBMISSION:
            return 'border-iota-requested';
        case RequestDocumentStatusLabel.RESUBMITTED:
            return 'border-iota-warning';
        default:
            return 'border-iota-draft';
    }
}

export interface RoleChip {
    color: 'primary' | 'accent';
    name: string;
}

export function companyRolesToChips(roles: CompanyRole[]): RoleChip[] {
    const arr: RoleChip[] = [];

    if (!roles) {
        return arr;
    }

    if (roles.includes(CompanyRole.CUSTOMER)) {
        arr.push({ color: 'primary', name: $localize`Customer` });
    }

    if (roles.includes(CompanyRole.VENDOR)) {
        arr.push({ color: 'accent', name: $localize`Solution Partner` });
    }

    return arr;
}

export function generateRegistrationPeriodChartData(registrationPeriods: CompanyRegistrationPeriodDTO[]): {
    min: number;
    max: number;
    chartData: Map<ChartPeriodName, CustomChartData>;
} {
    const chartData = new Map<ChartPeriodName, CustomChartData>();

    const minEffectiveFrom = Math.min(
        ...registrationPeriods.map((elem) => new Date(elem.effectiveFrom).getTime()),
        ...registrationPeriods.map((elem) => new Date(elem.registrationPeriodFrom)?.getTime())
    );
    const maxEffectiveFrom = Math.max(
        ...registrationPeriods.map((elem) => new Date(elem.effectiveFrom).getTime()),
        ...registrationPeriods.map((elem) => new Date(elem.registrationPeriodTo)?.getTime())
    );
    let maxEffectiveTo = Math.max(...registrationPeriods.map((elem) => new Date(elem.effectiveTo).getTime()));

    if (!maxEffectiveTo || registrationPeriods.some((elem) => !elem.effectiveTo)) {
        maxEffectiveTo = moment(new Date(maxEffectiveFrom)).utc().add(1, 'year').toDate().getTime();
    }

    registrationPeriods.forEach((registrationPeriod) => {
        let name = getAttributeFirstValue(registrationPeriod, RegistrationPeriodAttribute.INTERNAL_NAME);
        if (!name) {
            name = getAttributeFirstValue(registrationPeriod, RegistrationPeriodAttribute.NAME);
        }

        if (registrationPeriod.registrationPeriodTo) {
            const periodName: ChartPeriodName = 'Registrierungszeitraum';
            const period: CustomChartData = chartData.get(periodName) ?? { periodName, data: [] };

            period.data.push({
                x: name,
                y: [
                    new Date(registrationPeriod.registrationPeriodFrom).getTime(),
                    new Date(registrationPeriod.registrationPeriodTo).getTime()
                ]
            });

            chartData.set(periodName, period);
        } else {
            const periodName: ChartPeriodName = 'Registrierungszeitraum (open-end)';
            const period: CustomChartData = chartData.get(periodName) ?? { periodName, data: [] };

            period.data.push({
                x: name,
                y: [
                    new Date(registrationPeriod.registrationPeriodFrom).getTime(),
                    moment(registrationPeriod.registrationPeriodFrom).utc().add(1, 'year').toDate().getTime()
                ]
            });

            chartData.set(periodName, period);
        }

        if (registrationPeriod.effectiveTo) {
            const periodName: ChartPeriodName = 'Produktperiode';
            const period: CustomChartData = chartData.get(periodName) ?? { periodName, data: [] };

            period.data.push({
                x: name,
                y: [new Date(registrationPeriod.effectiveFrom).getTime(), new Date(registrationPeriod.effectiveTo).getTime()]
            });

            chartData.set(periodName, period);
        } else {
            const periodName: ChartPeriodName = 'Produktperiode (open-end)';
            const period: CustomChartData = chartData.get(periodName) ?? { periodName, data: [] };

            period.data.push({
                x: name,
                y: [
                    new Date(registrationPeriod.effectiveFrom).getTime(),
                    moment(registrationPeriod.effectiveFrom).utc().add(1, 'year').toDate().getTime()
                ]
            });

            chartData.set(periodName, period);
        }
    });

    return {
        min: minEffectiveFrom,
        max: maxEffectiveTo,
        chartData
    };
}

export function excelSerialToJsDate(xlSerial: number): Date {
    if (!xlSerial || isNaN(xlSerial)) {
        return undefined;
    }

    return new Date(-2209075200000 + (xlSerial - (xlSerial < 61 ? 0 : 1)) * 86400000);
}

export function waitForBooleanValue(store: Store, selector: (state: object) => boolean, targetValue: boolean): Promise<void> {
    return new Promise((res) =>
        store
            .select(selector)
            .pipe(
                filter((val) => val === targetValue),
                take(1)
            )
            .subscribe(() => res())
    );
}

export function getValueFromStore<T>(store: Store, selector: (state: any) => T): Promise<T> {
    return new Promise((res) =>
        store
            .select(selector)
            .pipe(take(1))
            .subscribe((elem) => res(elem))
    );
}

export function waitForValueNotFalsy<T>(store: Store, selector: (state: any) => T): Promise<T> {
    return new Promise((res) =>
        store
            .select(selector)
            .pipe(
                filter((v) => !!v),
                take(1)
            )
            .subscribe((elem) => res(elem))
    );
}

export function getFirstActionValue<T>(
    actions: Actions,
    successAction: ActionCreator<string, (props: T) => T & Action<string>>,
    failureAction: ActionCreator<string, (props: { error?: any }) => { error?: any } & Action<string>>
): Promise<T> {
    return new Promise((resolve, reject) =>
        actions
            .pipe(
                ofType(successAction, failureAction),
                take(1),
                catchError((error) => {
                    reject(error);
                    return throwError(() => error);
                })
            )
            .subscribe((action) => {
                if (successAction.type === action.type) {
                    resolve(action as T);
                } else {
                    reject(action);
                }
            })
    );
}

export function jsonStringifyArrayAndConcatenate(arr: Array<any>, separator: string): string {
    if (arr) {
        if (arr.length === 1) {
            return JSON.stringify(arr[0]);
        } else {
            return arr.map((x) => JSON.stringify(x)).reduce((a, b) => a + `${b}${separator}`, '');
        }
    }
    return '';
}

export function getLocationHeader(response: HttpResponse<any>): string {
    const locationHeader = response.headers.get('location');
    if (locationHeader) {
        const parts = locationHeader.split('/');
        return parts[parts.length - 1];
    }
    return undefined;
}

export function getFilenameHeader(response: HttpResponse<any>, defaultFilename: string): string {
    const contentDispositionHeader = response?.headers?.get('content-disposition');
    if (contentDispositionHeader) {
        const match = contentDispositionHeader.match(/filename="([^"]+)"/);
        return match ? match?.[1] : defaultFilename;
    }
    return defaultFilename;
}

export function getPropertyAsString(property: any, options?: { formatNumberAsCurrency?: boolean }): string {
    if (property === undefined || property === null) {
        return '';
    }

    // Reads local calendar components directly, for the same reason as parseApiDate below.
    if (property instanceof Date || /^\d{4}-\d{2}-\d{2}/.test(property as string)) {
        return moment(property).format('DD.MM.YYYY');
    }

    // Currency amounts are shown with two decimal places
    if (typeof property === 'number' && options?.formatNumberAsCurrency) {
        return property.toFixed(2);
    }

    return property as string;
}

export function getFilteredSmartTableRows<TRow extends SmartTableRow>(
    rows: TRow[],
    headers: readonly SmartTableHeader[],
    searchString?: string
): TRow[] {
    if (!searchString || !searchString?.trim()) return rows;

    const searchableCols = headers?.filter((header) => !header?.hidden || header?.searchable)?.map((header) => header?.key);

    // Check all columns for the string
    const lowerCaseSearchString = searchString.toLowerCase().trim();

    return rows.filter((row) => {
        if (searchString) {
            const allEntries = Object.entries(row);
            const allData = allEntries?.filter(([key]) => searchableCols?.includes(key))?.map(([, value]) => value?.data);

            return allData?.some((data) => data?.toLowerCase().includes(lowerCaseSearchString));
        }

        return true;
    });
}

/**
 * Returns `true` if a redirect to the `/en/` or `/de/` URL was started. The page is about to be
 * replaced, so the app must not bootstrap.
 */
export function normalizeLanguageUrl(): boolean {
    const currentUrl = window.location.pathname;
    const searchAndHash = window.location.search + window.location.hash;

    if (currentUrl.startsWith('/en/') || currentUrl.startsWith('/de/')) {
        return false;
    }

    if (currentUrl === '/en') {
        window.location.href = '/en/' + searchAndHash;
        return true;
    }
    if (currentUrl === '/de') {
        window.location.href = '/de/' + searchAndHash;
        return true;
    }

    const browserLang = getInhLanguageFromBrowser();
    window.location.href = `/${browserLang.countryCode}${currentUrl}${searchAndHash}`;
    return true;
}

export function getCurrentLanguagePath(): string {
    const currentLang = getCurrentLanguageFromPath();
    return currentLang ? '/' + currentLang.countryCode : '';
}

export function getCurrentLanguageFromPath(): InhLanguage {
    const currentUrl = window.location.pathname;
    const index = currentUrl.indexOf('/', 1);

    if (index > -1) {
        const langFromPath = currentUrl.substring(1, index);

        return AvailableLanguages.find((l) => l.countryCode === langFromPath);
    }

    return undefined;
}

export function getInhLanguageFromBrowser(): InhLanguage {
    return AvailableLanguages.find((al) => navigator.language.includes(al.countryCode)) || DefaultLanguage;
}

/** The language the running bundle was translated into; it is also correct when the url carries no language prefix. */
export function getCurrentLanguageFromLocale(): InhLanguage {
    return InhLanguages[getLanguageCodeFromLocaleOrDefault($localize.locale)];
}

export function getLanguageCodeFromLocaleOrDefault(locale: string): InhLanguageCountryCode {
    const languageCode = locale?.split('-')[0];
    return AvailableLanguages.find((language) => language.countryCode === languageCode)?.countryCode ?? DefaultLanguage.countryCode;
}

export function getIotaConfigAttributeDescription(attribute: IotaConfigAttribute, countryCode: string): string {
    // If description for selected country code exists, return this description
    const selectedLanguageDescription = attribute?.description?.[countryCode];
    if (selectedLanguageDescription) return selectedLanguageDescription;

    // Fallback to german description
    const germanDescription = attribute?.description?.['de'];
    if (germanDescription) return germanDescription;

    // Fallback to english description
    return attribute?.description?.['en'];
}

export function getIotaConfigAttributeTooltip(attribute: IotaConfigAttribute, countryCode: string): string {
    // If tooltip for selected country code exists, return this tooltip
    const selectedLanguageTooltip = attribute?.tooltip?.[countryCode];
    if (selectedLanguageTooltip) return selectedLanguageTooltip;

    // Fallback to german tooltip
    const germanTooltip = attribute?.tooltip?.['de'];
    if (germanTooltip) return germanTooltip;

    // Fallback to english tooltip
    const englishTooltip = attribute?.tooltip?.['en'];
    if (englishTooltip) return englishTooltip;

    return undefined;
}

export enum KnownFileTypes {
    IMAGE = 'image',
    PDF = 'pdf',
    VIDEO = 'video',
    AUDIO = 'audio',
    PRESENTATION = 'presentation',
    SPREADSHEET = 'spreadsheet',
    DOCUMENT = 'document',
    CALENDAR_INVITATION = 'calendar_invitation',
    OTHER = 'other'
}

export function getFileIconExtension(document: DocumentDescriptionDTO): string {
    return document?.name.split('.').slice(-1).join('').toLowerCase();
}

export function getFileType(attachment: DocumentDescriptionDTO): KnownFileTypes {
    console.log('got attchmnet', attachment);
    //in fact, attachment can be a JS File
    const contentType = attachment.contentType ?? attachment['type'] ?? '';
    const extension = getFileIconExtension(attachment);
    if (contentType.startsWith('image')) {
        return KnownFileTypes.IMAGE;
    } else if (contentType === 'application/pdf') {
        return KnownFileTypes.PDF;
    } else if (contentType.startsWith('video')) {
        return KnownFileTypes.VIDEO;
    } else if (contentType.startsWith('audio')) {
        return KnownFileTypes.AUDIO;
    } else if (
        contentType === 'application/vnd.ms-powerpoint' ||
        contentType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        contentType === 'application/vnd.oasis.opendocument.presentation' ||
        contentType === 'application/vnd.apple.keynote' ||
        extension === 'odp'
    ) {
        return KnownFileTypes.PRESENTATION;
    } else if (
        contentType === 'application/vnd.ms-excel' ||
        contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheets' ||
        contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        contentType === 'application/vnd.apple.pages' ||
        extension === 'ods'
    ) {
        return KnownFileTypes.SPREADSHEET;
    } else if (
        contentType.startsWith('application/vnd.ms') ||
        contentType.startsWith('application/vnd.openxmlformats') ||
        contentType === 'application/vnd.apple.pages' ||
        contentType === 'application/msword' ||
        extension === 'odt'
    ) {
        return KnownFileTypes.DOCUMENT;
    } else if (
        contentType === 'text/calendar' ||
        extension === 'ics' ||
        extension === 'ifb' ||
        extension === 'ical' ||
        extension === 'icalendar'
    ) {
        return KnownFileTypes.CALENDAR_INVITATION;
    } else {
        return KnownFileTypes.OTHER;
    }
}

export function getHeroFileIcon(document: DocumentDescriptionDTO): string {
    const fileType = getFileType(document);
    switch (fileType) {
        case KnownFileTypes.IMAGE:
            return 'photo';
        case KnownFileTypes.OTHER:
        default:
            return 'document-text';
    }
}

export function getRegistrationPeriodForCompanyEmployee(
    companyRegistrationPeriods: CompanyRegistrationPeriodDTO[],
    companyEmployee: Pick<BasicEmployeeView, 'employeeGroup'>
) {
    return companyRegistrationPeriods?.find((period) => period.selectionKey === companyEmployee?.employeeGroup);
}

export function isEmployeeRegistrationPossibleFromSameOrAfterRegistrationAllowedFrom(
    companyRegistrationPeriod: CompanyRegistrationPeriodDTO,
    companyEmployee: Pick<BasicEmployeeView, 'registrationPossibleFrom'>
): boolean {
    if (!companyRegistrationPeriod?.registrationAllowedFrom || !companyEmployee?.registrationPossibleFrom) return true;

    // Date on employee must be same or after date on registration period
    return moment(companyEmployee?.registrationPossibleFrom).isSameOrAfter(moment(companyRegistrationPeriod?.registrationAllowedFrom));
}

export function getNumberFormattedWithDecimalPlaces(value: number, minDecimalPlaces = 2): string | number {
    if (value === undefined || value === null) return value;

    const valueAsString = value.toString();
    if (valueAsString.includes('.')) {
        const numberOfDigits = valueAsString.split('.')[1].length;
        if (numberOfDigits > minDecimalPlaces) {
            return value.toFixed(numberOfDigits);
        }
    }

    return value.toFixed(minDecimalPlaces);
}

export function resolveCompanyUrl(company: CompanyEntity | CompanyDTO): string {
    if (company?.roles.includes(CompanyRole.CUSTOMER)) {
        return '/administration/customers/' + company.companyId;
    } else if (company?.roles.includes(CompanyRole.VENDOR)) {
        return '/administration/solution-partners/' + company.companyId;
    }

    return null;
}

export async function getCompanyUrlById(store: Store, companyId: string): Promise<string> {
    if (!companyId) {
        return null;
    }

    await waitForBooleanValue(store, companiesLoading, false);
    const allCompanies = await getValueFromStore(store, companies);
    const company = allCompanies.find((company) => company.companyId === companyId);

    return resolveCompanyUrl(company);
}

export const countryCodes = [
    'DE',
    'AT',
    'LU',
    'FR',
    '-',
    ...Object.keys(CountryCode).filter((code) => !['DE', 'AT', 'LU', 'FR'].includes(code))
].map((key) => CountryCode[key]);

export function getFilteredAuditInvoiceSummaries(
    allAuditInvoiceSummaries: AuditInvoiceSummaryEntity[],
    filter: AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER,
    filteredBusinessPartners: string[] = [],
    filteredDocumentDateFrom: Date = undefined,
    filteredDocumentDateTo: Date = undefined
): AuditInvoiceSummaryEntity[] {
    return allAuditInvoiceSummaries.filter((auditInvoiceSummary) => {
        if (
            filteredBusinessPartners?.length > 0 &&
            !!auditInvoiceSummary.companyId &&
            !filteredBusinessPartners.includes(auditInvoiceSummary.companyId)
        ) {
            return false;
        }
        if (
            filteredBusinessPartners?.length > 0 &&
            !!auditInvoiceSummary.centralRegulatorId &&
            !filteredBusinessPartners.includes(auditInvoiceSummary.centralRegulatorId)
        ) {
            return false;
        }

        if (!!filteredDocumentDateFrom && isAfterAll(filteredDocumentDateFrom, [auditInvoiceSummary.creationDateTime])) {
            return false;
        }
        if (!!filteredDocumentDateTo && isBeforeAll(filteredDocumentDateTo, [auditInvoiceSummary.creationDateTime])) {
            return false;
        }

        if (
            filter === AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER.APPROVED &&
            auditInvoiceSummary?.invoiceStatus !== InvoiceStatus.REVIEWED_AND_APPROVED
        ) {
            return false;
        }
        if (
            filter === AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER.REJECTED &&
            auditInvoiceSummary?.invoiceStatus !== InvoiceStatus.REVIEWED_AND_REJECTED
        ) {
            return false;
        }
        return !(
            filter === AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER.FAILED &&
            auditInvoiceSummary?.invoiceStatus !== InvoiceStatus.GENERATION_FAILED
        );
    });
}

export function getFeedbackBorderBackgroundColors(): string {
    switch (this.variant) {
        case 'error':
            return 'bg-iota-error';
        case 'warning':
            return 'bg-iota-warning';
        case 'confirmation':
            return 'bg-iota-inh-green';
        default:
            return 'bg-iota-inh-green';
    }
}

export function getFeedbackBorderColors(variant: FeedbackBannerVariant): string {
    switch (variant) {
        case 'error':
            return 'border-iota-error';
        case 'warning':
            return 'border-iota-warning';
        case 'confirmation':
            return 'border-iota-inh-green';
        default:
            return 'border-iota-outline';
    }
}

export function getFeedbackMessageColors(): string {
    switch (this.variant) {
        case 'error':
            return 'text-iota-error';
        case 'warning':
            return 'bg-iota-warning text-white';
        case 'confirmation':
            return 'bg-iota-inh-green text-white';
        default:
            return 'bg-iota-inh-green text-white';
    }
}

export function getFeedbackIcon(): string {
    switch (this.variant) {
        case 'error':
            return 'exclamation-triangle';
        case 'warning':
            return 'exclamation-circle';
        case 'confirmation':
            return 'check-circle';
        default:
            return 'check-circle';
    }
}

export function sortByNameWithAlphabeticCharactersFirst<T extends { name?: string }>(entities: T[]) {
    if (!entities) return entities;

    return [...entities].sort((a, b) => {
        if (!a.name && !b.name) return 0;
        if (!a.name) return 1;
        if (!b.name) return -1;

        // Sort by name, but put entities that don't start with a letter last
        const aStartsWithLetter = a.name.match(/^[a-zA-Z]/);
        const bStartsWithLetter = b.name.match(/^[a-zA-Z]/);

        if (aStartsWithLetter && !bStartsWithLetter) return -1;
        if (!aStartsWithLetter && bStartsWithLetter) return 1;

        return a.name.localeCompare(b.name);
    });
}

export function getFilteredPayrollReportSummaries(
    allPayrollReportSummaries: PayrollReportSummaryEntity[],
    filter: PAYROLL_REPORT_SUMMARY_OVERVIEW_FILTER,
    filteredCompanies: string[] = [],
    filteredDateFrom: Date = undefined,
    filteredDateTo: Date = undefined
): PayrollReportSummaryEntity[] {
    return allPayrollReportSummaries.filter((payrollReportSummary) => {
        if (filteredCompanies?.length > 0 && !filteredCompanies.includes(payrollReportSummary.companyId)) {
            return false;
        }

        if (
            !!filteredDateFrom &&
            isAfterAll(filteredDateFrom, [payrollReportSummary.creationDateTime, payrollReportSummary.reportToDate])
        ) {
            return false;
        }

        if (!!filteredDateTo && isBeforeAll(filteredDateTo, [payrollReportSummary.creationDateTime, payrollReportSummary.reportFromDate])) {
            return false;
        }

        if (
            filter === PAYROLL_REPORT_SUMMARY_OVERVIEW_FILTER.APPROVED &&
            payrollReportSummary?.status !== PayrollReportSummaryStatus.REVIEWED_AND_APPROVED
        ) {
            return false;
        }
        if (
            filter === PAYROLL_REPORT_SUMMARY_OVERVIEW_FILTER.REJECTED &&
            payrollReportSummary?.status !== PayrollReportSummaryStatus.REVIEWED_AND_REJECTED
        ) {
            return false;
        }
        return !(
            filter === PAYROLL_REPORT_SUMMARY_OVERVIEW_FILTER.FAILED &&
            payrollReportSummary?.status !== PayrollReportSummaryStatus.GENERATION_FAILED
        );
    });
}

export function formatDate(value?: string, format = 'DD.MM.YYYY'): string | undefined {
    if (!value) {
        return undefined;
    }
    const date = moment(value);
    return date.isValid() ? date.format(format) : undefined;
}

/**
 * Formats a value for an API field of `format: date` (`YYYY-MM-DD`).
 * Reads the local calendar components on purpose: a Date at local midnight formatted with
 * UTC components would fall back to the previous day for a German user (14.08. -> 13.08.).
 */
export function toApiDateString(value: Date | string | null | undefined): string | null {
    return value ? moment(value).format('YYYY-MM-DD') : null;
}

/**
 * Parses an API value of `format: date` into a Date at local midnight, or one of `format: date-time`
 * into a Date keeping its time of day.
 * `new Date('2026-08-14')` would yield UTC midnight, which renders as the previous day
 * west of Greenwich and mixes semantics with every other Date in this app.
 */
export function parseApiDate(value: string | null | undefined): Date | null {
    return value ? moment(value).toDate() : null;
}

// The portal client types its timestamps as `Date` but the wire sends strings, so moment parses both.
export function toIsoTimestamp(value: Date | string): string | null {
    return value ? moment(value).toISOString() : null;
}

export function isBeforeAll(
    dateThatShouldBeBeforeAll: string | Date,
    datesToCheck: (string | Date)[],
    granularity: moment.unitOfTime.StartOf = 'day'
): boolean {
    if (!dateThatShouldBeBeforeAll || !datesToCheck) return false;

    return datesToCheck.every((date) => moment(dateThatShouldBeBeforeAll).isBefore(moment(date), granularity));
}

export function isAfterAll(
    dateThatShouldBeAftereAll: string | Date,
    datesToCheck: (string | Date)[],
    granularity: moment.unitOfTime.StartOf = 'day'
): boolean {
    if (!dateThatShouldBeAftereAll || !datesToCheck) return false;

    return datesToCheck.every((date) => moment(dateThatShouldBeAftereAll).isAfter(moment(date), granularity));
}

// company deactivation helper functions
export function getCompanyName(company: CompanyEntity): string {
    return company?.name + ' (' + company?.companyNumber + ')';
}

export function getFirstDeactivatedCompanyIndex(companies: CompanyEntity[]): number {
    if (!companies) return -1;
    return companies.findIndex((c) => isCompanyDeactivated(c));
}

export function isCompanyDeactivated(company: CompanyEntity): boolean {
    return company?.isDeactivated === true;
}

export function sortCompaniesByDeactivationStatus(companies: CompanyEntity[]): CompanyEntity[] {
    const activeCompanies = companies.filter((c) => !isCompanyDeactivated(c));
    const deactivatedCompanies = companies.filter((c) => isCompanyDeactivated(c));
    return [...activeCompanies, ...deactivatedCompanies];
}

// office deactivation helper functions
export function getFirstDeactivatedOfficeIndex(offices: OfficeDTO[]): number {
    if (!offices) return -1;
    return offices.findIndex((office) => office?.deactivated === true);
}

export function sortOfficesByDeactivationStatus(offices: OfficeDTO[]): OfficeDTO[] {
    const activeOffices = offices.filter((office) => office?.deactivated !== true);
    const deactivatedOffices = offices.filter((office) => office?.deactivated === true);
    return [...activeOffices, ...deactivatedOffices];
}

export function isOfficeDeactivated(office: OfficeDTO): boolean {
    return office?.deactivated === true;
}

export function getActiveOffices(offices: OfficeDTO[]): OfficeDTO[] {
    return (offices ?? []).filter((office) => !isOfficeDeactivated(office));
}
